import { defineStore } from 'pinia'
import { ref } from 'vue'
import * as GameApi from '@/api/game'
import { useAppStore } from '@/stores/modules/app'
import { getDeviceFingerprint, getCookie, getUrlParam } from '@/utils/pub-use'
import { APKTracking } from '@/utils/event-tracking'
import useRouteCacheStore from '@/stores/modules/routeCache'
import { getTerminalType } from '@/utils/device'

// 用户信息接口
interface UserInfo {
  id?: number
  userId: number
  accessToken: string
  refreshToken: string
  expiresTime: number
  siteMsg: any
  userWalletInfos?: Array<{
    availableAccount: string
    // 其他钱包相关字段...
  }>
  firstRechargeRebate?: boolean // 添加首充返利字段
  todayBet?: number // 添加今日投注字段
}

// 登录表单接口
interface LoginForm {
  phone: string
  password: string
  areaCode: string
  platform: number
  deviceNumber?: string // 添加设备指纹字段
  channelId?: string // 添加渠道ID字段
}

// 注册表单接口
interface RegisterForm {
  phone: string
  password: string
  areaCode: string
  platform: number
  inviteCode?: string
  deviceNumber?: string // 添加设备指纹字段
  channelId?: string // 添加渠道ID字段
}

// 初始化用户信息
const InitUserInfo: UserInfo = {
  userId: 0,
  accessToken: '',
  refreshToken: '',
  expiresTime: 0,
  siteMsg: null,
}

export const useUserStore = defineStore('user', () => {
  const userInfo = ref<UserInfo>({ ...InitUserInfo })
  const isLoggedIn = ref(false)

  // 设置用户信息
  const setUserInfo = (info: UserInfo) => {
    userInfo.value = { ...info }
    isLoggedIn.value = true
  }

  // 清除用户信息
  const clearUserInfo = () => {
    userInfo.value = { ...InitUserInfo }
    isLoggedIn.value = false
    
    // 只清除路由缓存，不影响其他用户信息
    const routeCacheStore = useRouteCacheStore()
    routeCacheStore.clearRouteCache()
    
    // 清除sessionStorage中的TabBar相关标记
    sessionStorage.removeItem('fromPerson')
    sessionStorage.removeItem('fromHome')
    
    // 不清除弹窗相关的本地存储，只清除认证相关的数据
    const keysToRemove = [
      'access_token',
      'refresh_token',
      'user_id',
      'user-store',
      'rememberedCredentials'
    ]
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
    })
    
    // 注意：保留以下弹窗相关的本地存储：
    // - stable_user_id
    // - popup_daily_*
    // - popup_once_*
    // - popup_no_reminder_*
    // - popup_visit_*
    // - popup_login_visit_*
    // - popup_login_timestamp_*
    // - popup_activity_*
  }

  // 获取 Authorization 头部
  const getAuthorizationHeader = () => {
    if (userInfo.value.accessToken) {
      return `Bearer ${userInfo.value.accessToken}`
    }
    return ''
  }

  // 登录
  const login = async (loginForm: LoginForm) => {
    try {
      // 获取设备指纹
      const deviceNumber = await getDeviceFingerprint()
      
      // 获取渠道ID
      const appStore = useAppStore()
      const channelId = appStore.channelInfo?.id?.toString() || ''
      
      // 获取终端类型
      const terminalType = getTerminalType(channelId)

      const response = await GameApi.userlogin({
        ...loginForm,
        deviceNumber, // 添加设备指纹
        channelId, // 添加渠道ID
        terminalType // 添加终端类型
      })

      if (response.code === 0 || response.code === 200) {
        // 保存用户信息到 store
        setUserInfo(response.data)

        // 保存 token 到 localStorage
        localStorage.setItem('access_token', response.data.accessToken)
        localStorage.setItem('refresh_token', response.data.refreshToken)
        localStorage.setItem('user_id', response.data.userId.toString())

        // 立即触发socket认证
        if (window.$socket) {
          try {
            // 添加超时机制，避免Socket认证卡住整个注册流程
            const socketAuthPromise = window.$socket.doAuth()
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Socket认证超时')), 3000) // 3秒超时
            })
            
            await Promise.race([socketAuthPromise, timeoutPromise])
            console.log('✅ Socket认证成功')
          } catch (error) {
            console.error('❌ Socket认证失败:', error)
            // Socket认证失败不应该影响登录流程，继续执行后续操作
          }
        }

        // 获取用户详细信息
        await info()

        // 登录成功后检查活动红点状态
        try {
          const { checkAllActivityBadges } = await import('@/utils/activity-badge')
          await checkAllActivityBadges()
        } catch (error) {
          console.error('检查活动红点失败:', error)
        }
        const apkTracking = new APKTracking()
        // 上报用户登录事件
        apkTracking.trackLogin()

        const adjustTracking = window.$adjustTracking
        if (adjustTracking) {
          adjustTracking.trackLogin()
        }
        const appsFlyerTracking = window.$appsFlyerTracking
        if (appsFlyerTracking) {
          appsFlyerTracking.trackLogin()
        }
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Login')
        }
        return response
      }
      else {
        throw new Error(response.msg || 'Login failed')
      }
    }
    catch (error) {
      clearUserInfo()
      throw error
    }
  }

  // 注册
  const register = async (registerForm: RegisterForm) => {
    const trackAdInfo = {}
    try {
      const adjustTracking = window.$adjustTracking
      // let web_uuid = undefined, fbc = undefined
      if (adjustTracking) {
        const webUuid = adjustTracking.webUuid
        trackAdInfo['adjust'] = {
          webUuid
        }
      }
      if (typeof window.fbq === 'function') {
        let fbc = getCookie('_fbc')
        const fbclid = getUrlParam("fbclid");
        if (!fbc && fbclid) {
          const time = Date.now();
          fbc = `fb.1.${time}.${fbclid}`;
        }
        trackAdInfo['facebook'] = {
          fbc
        }
      }
      const deviceNumber = await getDeviceFingerprint()
      
      // 获取渠道ID
      const appStore = useAppStore()
      const channelId = appStore.channelInfo?.id?.toString() || ''
      
      // 获取终端类型
      const terminalType = getTerminalType(channelId)
      
      const regData = {
        ...registerForm,
        deviceNumber,
        channelId, // 添加渠道ID
        terminalType, // 添加终端类型
        trackAdInfo
      }
      const response = await GameApi.userreg(regData)
      if (response.code === 0 || response.code === 200) {
        const apkTracking = new APKTracking()
        // 上报用户注册事件
        apkTracking.trackRegister()
        if (adjustTracking) {
          adjustTracking.trackRegister()
        }
        const appsFlyerTracking = window.$appsFlyerTracking
        if (appsFlyerTracking) {
          appsFlyerTracking.trackRegister()
        }
        // 上报 Facebook Pixel 注册完成事件
        const { trackCompleteRegistration } = await import('@/utils/facebook-pixel-tracking')
        trackCompleteRegistration()
        // 注册成功后直接登录
        const loginResult = await login(registerForm)
        return loginResult
      }
      else {
        throw new Error(response.msg || 'Registration failed')
      }
    }
    catch (error) {
      throw error
    }
  }

  // 获取用户信息 - 改名为 info
  const info = async () => {
    try {
      const response = await GameApi.info()

      if (response.code === 0 || response.code === 200) {
        // 更新用户信息，保留原有的 token 等信息
        setUserInfo({
          ...userInfo.value,
          ...response.data,
        })
        return response
      }
      else {
        throw new Error(response.msg || 'Failed to get user info')
      }
    }
    catch (error) {
      clearUserInfo()
      throw error
    }
  }

  // 登出
  const logout = async () => {
    try {
      // 如果有登出接口，可以调用
      await GameApi.logout()
      const apkTracking = new APKTracking()
      // 上报用户登出事件
      apkTracking.trackLogout()
      const adjustTracking = window.$adjustTracking
      if (adjustTracking) {
        adjustTracking.trackLogout()
      }
      const appsFlyerTracking = window.$appsFlyerTracking
      if (appsFlyerTracking) {
        appsFlyerTracking.trackLogout()
      }
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Logout')
      }
    }
    finally {
      // 清除认证相关的本地存储
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_id')

      // 清除 persist 数据，但保留弹窗相关数据
      localStorage.removeItem('user-store')

      // 注意：不清空以下弹窗相关的本地存储：
      // - stable_user_id
      // - popup_daily_*
      // - popup_no_reminder_*
      // - popup_visit_*
      // - popup_login_visit_*
      // - popup_refresh_visit_*

      // 清除 store 中的用户信息
      clearUserInfo()
      
    }
  }

  // 检查是否已登录
  const checkLoginStatus = () => {
    const token = localStorage.getItem('access_token')
    const userId = localStorage.getItem('user_id')

    if (token && userId) {
      // 如果有 token 和 userId，尝试恢复登录状态
      userInfo.value.accessToken = token
      userInfo.value.userId = Number.parseInt(userId)
      userInfo.value.refreshToken = localStorage.getItem('refresh_token') || ''
      isLoggedIn.value = true

      // 可以在这里验证 token 是否有效
      return true
    }

    return false
  }

  // 更新用户钱包信息
  const updateUserWallet = (walletInfo: any) => {
    // 如果walletInfo是数组，直接使用
    if (Array.isArray(walletInfo)) {
      userInfo.value.userWalletInfos = walletInfo
    }
    // 如果walletInfo是对象，包装成数组
    else if (walletInfo && typeof walletInfo === 'object') {
      userInfo.value.userWalletInfos = [walletInfo]
    }
    // 如果walletInfo是数字或字符串，创建钱包对象
    else if (walletInfo !== undefined) {
      userInfo.value.userWalletInfos = [{
        availableAccount: walletInfo
      }]
    }
  }

  return {
    userInfo,
    isLoggedIn,
    login,
    register,
    info,
    getUserInfo: info,
    logout,
    setUserInfo,
    clearUserInfo,
    checkLoginStatus,
    updateUserWallet,
    getAuthorizationHeader,
  }
}, {
  persist: {
    key: 'user-store',
    storage: localStorage,
    // 使用 includes 而不是 paths
    includes: ['userInfo', 'isLoggedIn'],
  },
})

export default useUserStore
